import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useConfig } from './Config';

const BackgroundColorContext = createContext<
  | {
      availableBackgroundColors: string[];
      selectedBackgroundColor: string;
      setBackgroundColor: (_color: string) => void;
      resetBackgroundColor: () => void;
    }
  | undefined
>(undefined);

type BackgroundColorProviderProps = {
  children: ReactNode;
};

function BackgroundColorProvider({ children }: BackgroundColorProviderProps) {
  const { svgBackgroundColors, defaultSvgBackgroundColor } = useConfig();

  const availableColors = useMemo(() => {
    return (svgBackgroundColors || '')
      .split('|')
      .map(raw => raw.trim().toLowerCase())
      .filter(Boolean);
  }, [svgBackgroundColors]);

  const [color, setColor] = useState(() => {
    if (defaultSvgBackgroundColor && availableColors.includes(defaultSvgBackgroundColor.toLowerCase())) {
      return defaultSvgBackgroundColor.toLowerCase();
    }
    return null;
  });

  const setBackgroundColor = useCallback(
    (color: string) => {
      setColor(availableColors.includes(color) ? color : 'none');
    },
    [availableColors],
  );

  const resetBackgroundColor = useCallback(() => {
    setColor('none');
  }, []);

  const value = useMemo(
    () => ({
      availableBackgroundColors: availableColors,
      selectedBackgroundColor: color,
      setBackgroundColor,
      resetBackgroundColor,
    }),
    [color, availableColors, setBackgroundColor, resetBackgroundColor],
  );
  return <BackgroundColorContext.Provider value={value}>{children}</BackgroundColorContext.Provider>;
}

function useBackgroundColor() {
  const context = useContext(BackgroundColorContext);
  if (!context) {
    throw new Error(`useBackgroundColor must be used within a BackgroundColorProvider`);
  }
  return context;
}

export { BackgroundColorProvider, useBackgroundColor };
